/**
 * cliente http leve. usa fetch com credentials: 'include' (cookie de sessao).
 * lança ApiError em status >= 400 — TanStack Query captura.
 */

const BASE = '/api';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly issues?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface FetchOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const url = `${BASE}${path}`;
  // so envia Content-Type quando ha body — POST sem body com Content-Type
  // application/json faz o Fastify rejeitar com 400 "Body cannot be empty".
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    credentials: 'include',
    headers,
    ...(opts.signal && { signal: opts.signal }),
    ...(opts.body !== undefined && { body: JSON.stringify(opts.body) }),
  };

  const res = await fetch(url, init);
  const text = await res.text();
  const data = text
    ? (() => {
        try {
          return JSON.parse(text);
        } catch {
          return { _raw: text };
        }
      })()
    : null;

  if (!res.ok) {
    const code = (
      data && typeof data === 'object' && 'error' in data ? String(data.error) : 'HTTP_ERROR'
    ) as string;
    const msg = (
      data && typeof data === 'object' && 'message' in data ? String(data.message) : res.statusText
    ) as string;
    const issues = data && typeof data === 'object' && 'issues' in data ? data.issues : undefined;
    throw new ApiError(res.status, code, msg, issues);
  }
  return data as T;
}
